import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// External import
import {
    AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ListMetadataKeys } from '@data/models/metadata.model';
// Internal import
import {
    MagicboxDeleteMetadataDialogComponent,
} from '@shared/components/magicbox-table/magicbox-delete-metadata-dialog.component';
import { buildPageSizeOptions } from '@shared/utils/utilities';
import { MatInput } from '@angular/material/input';

const DEFAULT_LIST_SIZE = 10;

export class MagicboxMetadata {
    public key: string;
    public value: string;

    constructor(key: string, value: string) {
        this.key = key;
        this.value = value;
    }
}

@Component({

    selector: 'syn-magicbox-table',
    templateUrl: 'magicbox-table.component.html',
    styleUrls: ['magicbox-table.component.scss'],
})
export class MagicboxTableComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    @Output() onUpdateMetadata = new EventEmitter<Map<string, string>>();

    @Input() displayedColumns = ['key', 'value', 'action_buttons'];
    @Input() displayInCard = false;
    @Input() message: string;
    @Input() editable = true;
    @Input() pageSize: Number;
    @Input() pageSizeOptions: Number[] = [];
    @Input() metadataKeysLoader: Subject<string>;
    @Input() metadataKeys$ = new Observable<ListMetadataKeys>();

    public dataSource = new MatTableDataSource<MagicboxMetadata>();
    public showPaginator = false;
    public magicboxForm: FormGroup;
    public metadataSubscription: Subscription;

    public editingMetadataKey: string;
    public editingMetadataValue: string;

    private _metadata: Map<string, string>;

    constructor(public dialog: MatDialog) { }

    get metadata() {
        return this._metadata;
    }

    get metadataArray() {
        const metadataArray = [];
        for (const [key, value] of this._metadata) {
            metadataArray.push(new MagicboxMetadata(key, value));
        }
        return metadataArray;
    }

    @Input()
    set metadata(value: Map<string, string>) {
        this._metadata = value;
        this.dataSource.data = this.metadataArray;
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
    }

    ngOnInit() {
        this.pageSizeOptions = buildPageSizeOptions(this.metadataArray);
        this.pageSize = this.pageSizeOptions.length === 0 ? 0 : DEFAULT_LIST_SIZE;
        this.magicboxForm = new FormGroup({
            'key': new FormControl('', [
                Validators.required,
                Validators.pattern('^[a-z0-9]+((\/|-)[a-z0-9]+)*$'),
                this.keyUnicityValidator.bind(this)
            ]),
            'value': new FormControl('', Validators.required)
        });

        this.metadataSubscription = this.magicboxForm.controls['key'].valueChanges.pipe(
            debounceTime(600),
            distinctUntilChanged(),
        ).subscribe((filter) => {
            this.metadataKeysLoader.next(filter);
        });
    }

    ngOnDestroy(): void {
        this.metadataSubscription?.unsubscribe();
    }

    public applyFilter(filterValue: string, input?: MatInput) {
        if (input) {
            input.value = filterValue;
        }
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public addMetadata(formDirective: FormGroupDirective) {
        if (this.magicboxForm.valid) {
            this.metadata.set(this.magicboxForm.value.key, this.magicboxForm.value.value);
            formDirective.resetForm();
            this.magicboxForm.reset();
            this.onUpdateMetadata.emit(this.metadata);
        }
    }

    public deleteMetadata(key: string) {
        let deleteMetadataSubscription = new Subscription();

        const dialogRef = this.dialog.open(MagicboxDeleteMetadataDialogComponent, {
            data: { key: key },
            disableClose: true
        });

        deleteMetadataSubscription = dialogRef.componentInstance.onDeleteEmitter.subscribe(() => {
            this.metadata.delete(key);
            this.onUpdateMetadata.emit(this.metadata);
        });

        dialogRef.afterClosed().subscribe(() => {
            deleteMetadataSubscription.unsubscribe();
        });
    }

    public editMetadata(key: string) {
        this.editingMetadataKey = key;
        this.editingMetadataValue = this.metadata.get(key);
    }

    public cancelEditMetadata() {
        this.editingMetadataKey = null;
        this.editingMetadataValue = null;
    }

    public updateMetadata(key: string) {
        if (!this.editingMetadataValue) {
            return;
        }

        this.metadata.set(key, this.editingMetadataValue);
        this.editingMetadataKey = null;
        this.editingMetadataValue = null;

        this.onUpdateMetadata.emit(this.metadata);
    }

    public keyUnicityValidator(key: FormControl): { [s: string]: boolean } {
        if (this.metadata.has(key.value)) {
            return { keyNotUnique: true };
        }

        return null;
    }
}

